import { Alert, formWithValidation } from '@edgebox/react-components';
import { InternalId, StaticReference, Uuid } from '@edgebox/data-definition-kit';
import { ContentRepresentation, TaskGroupType } from '@edgebox/sync-core-data-definitions';
import { ClientProjectEntity, ClientTaskGroupDraft } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { IsBoolean, IsString, MaxLength } from 'class-validator';
import { Field } from 'formik';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { resolveProjectSite } from './Components/projectSite.utils';
import { useGovernanceRouteSync } from './useGovernanceRouteSync';
import { GovernanceOnboardingListItem } from './Onboarding/components/GovernanceOnboardingPrimitives';
import { GovernanceCrawlContentUrlInputForm } from './Components/GovernanceCrawlContentUrlForm';
import { useBrandPresenceOnboarding } from './BrandPresenceOnboardingProvider';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import { BP_CARD_CLASSES, BP_PRIMARY_BUTTON_CLASSES, BP_TOOLBAR_BUTTON_CLASSES } from './Components/brandPresenceClasses';

export interface IGovernanceCrawlingSettingsFormData {
  autoCrawlPages: boolean;
  selectionFormula: string;
}

export class GovernanceCrawlingSettingsFormData implements IGovernanceCrawlingSettingsFormData {
  @IsBoolean()
  autoCrawlPages!: boolean;

  @IsString()
  @MaxLength(5000)
  selectionFormula!: string;
}

const GovernanceCrawlingSettingsForm = formWithValidation<GovernanceCrawlingSettingsFormData, GovernanceCrawlingSettingsFormData>(
  (values) => plainToInstance(GovernanceCrawlingSettingsFormData, values),
  (entity) => instanceToPlain(entity) as GovernanceCrawlingSettingsFormData
);

interface IJwtPayload {
  user?: {
    type?: string;
    internalRoles?: unknown;
  };
}

function getIsInternalUser(jwt?: string): boolean {
  if (!jwt) {
    return false;
  }

  const payload = jwtDecode<IJwtPayload>(jwt);
  const user = payload?.user;

  if (user?.type === 'Internal') {
    return true;
  }

  if (Array.isArray(user?.internalRoles) && user.internalRoles.length > 0) {
    return true;
  }

  return false;
}

export function GovernanceCrawlingSettings() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const projectUuid = appParams?.pageOptions?.projectUuid as Uuid | undefined;

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | undefined>(undefined);
  const [project, setProject] = React.useState<ClientProjectEntity | undefined>(undefined);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | undefined>(undefined);
  const [lastSaved, setLastSaved] = React.useState<IGovernanceCrawlingSettingsFormData | undefined>(undefined);

  const [crawlUrls, setCrawlUrls] = React.useState<string[]>([]);
  const [crawlInputVersion, setCrawlInputVersion] = React.useState(0);
  const [crawlSubmitting, setCrawlSubmitting] = React.useState(false);
  const [crawlSubmitError, setCrawlSubmitError] = React.useState<string | undefined>(undefined);
  const [crawlTaskGroupId, setCrawlTaskGroupId] = React.useState<InternalId | undefined>(undefined);

  const isInternalUser = React.useMemo(() => getIsInternalUser(appParams?.siteOptions?.jwt), [appParams?.siteOptions?.jwt]);
  const { adoptProject } = useBrandPresenceOnboarding();

  useGovernanceRouteSync({
    embedPrefix: '/brand-presence/settings/crawling',
    appPrefix: '/brand-presence/settings/crawling',
    defaultEmbedPath: '/brand-presence/settings/crawling',
  });

  React.useEffect(() => {
    if (!apiContext?.api || !projectUuid) {
      setLoading(false);
      setLoadError('Project context is missing.');
      return;
    }

    let active = true;

    void (async () => {
      try {
        setLoading(true);
        const loadedProject = await apiContext.api.service.projects.itemByUuid(projectUuid);
        if (!active) {
          return;
        }

        setProject(loadedProject);
        setLoadError(undefined);
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadError((error as Error)?.message || 'Failed to load project crawling settings.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, projectUuid]);

  const initialValues = React.useMemo(() => {
    const autoCrawl = project?.crawling?.autoCrawl || [];
    const selectionFormula = project?.crawling?.filterContent?.page?.expression || '';

    return plainToInstance(GovernanceCrawlingSettingsFormData, {
      autoCrawlPages: autoCrawl.includes(ContentRepresentation.Page),
      selectionFormula,
    });
  }, [project]);

  if (loading) {
    return (
      <SkeletonRegion label="Loading crawling settings" className="w-full h-full bg-slate-50">
        <div className="max-w-[1548px] mx-auto space-y-5 px-6 py-8">
          <div className="mb-3">
            <SkeletonBlock className="h-3 w-40" />
            <SkeletonBlock className="mt-3 h-8 w-48 rounded-lg" />
            <SkeletonBlock className="mt-3 h-4 w-96 max-w-full" />
          </div>
          {[0, 1].map((card) => (
            <div key={card} className={`${BP_CARD_CLASSES} space-y-4 p-6`}>
              <SkeletonBlock className="h-5 w-44" />
              <SkeletonBlock className="h-4 w-80 max-w-full" />
              <div className="space-y-3 rounded-xl p-5 ring-1 ring-inset ring-slate-200/80">
                <SkeletonBlock className="h-4 w-28" />
                <SkeletonBlock className="h-4 w-64" />
              </div>
            </div>
          ))}
        </div>
      </SkeletonRegion>
    );
  }

  if (loadError || !project) {
    return (
      <div className="p-6">
        <Alert variant="danger">{loadError || 'Project settings are unavailable.'}</Alert>
      </div>
    );
  }

  const onAddCrawlUrl = (url: string) => {
    setCrawlSubmitError(undefined);
    setCrawlTaskGroupId(undefined);

    setCrawlUrls((prev) => {
      if (!url || prev.includes(url)) {
        return prev;
      }

      return [...prev, url];
    });

    setCrawlInputVersion((value) => value + 1);
  };

  const onRemoveCrawlUrl = (url: string) => {
    setCrawlSubmitError(undefined);
    setCrawlTaskGroupId(undefined);
    setCrawlUrls((prev) => prev.filter((item) => item !== url));
  };

  return (
    <div className="relative w-full h-full bg-slate-50 bg-brand-page">
      <div className="relative max-w-[1548px] mx-auto space-y-5 px-6 py-8">
        <BrandPresencePageHeader
          eyebrow={
            <>
              Brand Presence <span className="text-slate-300">/</span> Settings
            </>
          }
          title="Crawling"
          description="Configure crawl behavior and start focused crawl jobs for specific URLs."
        />

        <div className={`${BP_CARD_CLASSES} p-6 motion-safe:animate-bp-fade-up`}>
          <h2 className="m-0 text-base font-bold text-slate-900">Crawling settings</h2>
          <p className="mt-1 text-sm text-slate-500">
            Configure automatic crawling behavior and content-selection formula for this project.
          </p>

          <GovernanceCrawlingSettingsForm
            key={project.id}
            initialValues={initialValues}
            onSubmit={async (values) => {
              if (!apiContext?.api || !project.id) {
                setSaveError('Project settings are unavailable.');
                return;
              }

              setSaving(true);
              setSaveError(undefined);

              try {
                const currentAutoCrawl = project.crawling?.autoCrawl || [];
                const nextAutoCrawl = values.autoCrawlPages
                  ? Array.from(new Set([...currentAutoCrawl, ContentRepresentation.Page]))
                  : currentAutoCrawl.filter((entry) => entry !== ContentRepresentation.Page);

                const nextSelectionFormula = isInternalUser ? values.selectionFormula : initialValues.selectionFormula;

                const nextCrawling = {
                  ...(project.crawling || {}),
                  autoCrawl: nextAutoCrawl,
                  filterContent: {
                    ...(project.crawling?.filterContent || {}),
                    page: {
                      ...(project.crawling?.filterContent?.page || {}),
                      expression: nextSelectionFormula,
                    },
                  },
                };

                const updatedProject = await apiContext.api.service.projects.updateCrawlingSettings(
                  project.id,
                  instanceToPlain(nextCrawling)
                );
                setProject(updatedProject);
                // The area holds one copy of the project for every screen that reads it, so the
                // saved settings reach the content list's indexing reminder without a reload.
                adoptProject(updatedProject);
                setLastSaved(values);
              } catch (error) {
                setSaveError((error as Error)?.message || 'Unable to save crawling settings.');
              } finally {
                setSaving(false);
              }
            }}
          >
            {() => (
              <div className="space-y-6 pt-5">
                {saveError ? <div className="text-sm text-danger">{saveError}</div> : null}

                <section className="rounded-xl p-5 ring-1 ring-inset ring-slate-200/80">
                  <h3 className="m-0 text-sm font-semibold text-slate-900">Auto-crawl</h3>
                  <p className="mt-1 text-sm text-slate-500">Enable automated page crawling for governance content updates.</p>

                  <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-700">
                    <Field
                      name={'autoCrawlPages'}
                      type={'checkbox'}
                      className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-primary focus-visible:ring-2 focus-visible:ring-primary/40"
                    />
                    <span>Automatically crawl pages</span>
                  </label>
                </section>

                <section className="rounded-xl p-5 ring-1 ring-inset ring-slate-200/80">
                  <h3 className="m-0 text-sm font-semibold text-slate-900">Selection formula</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Define a formula to control which crawled pages are included in governance processing.
                  </p>

                  {isInternalUser ? (
                    <Field
                      as={'textarea'}
                      name={'selectionFormula'}
                      rows={8}
                      placeholder={'Example: AND(STARTS_WITH(content_url, "https://example.com"), NOT(CONTAINS(content_url, "/preview/")))'}
                      className="mt-4 w-full rounded-lg border border-slate-200 bg-slate-50/60 p-3 font-eyebrow text-[0.8125rem] text-slate-800 transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
                    />
                  ) : (
                    <div className="mt-4 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 font-eyebrow text-[0.8125rem] text-slate-600 ring-1 ring-inset ring-slate-200/80">
                      {initialValues.selectionFormula || 'No selection formula configured.'}
                    </div>
                  )}

                  {!isInternalUser ? (
                    <p className="mt-2 text-xs text-slate-500">Formula editing is available for internal users only.</p>
                  ) : null}
                </section>

                <div className="flex items-center justify-end gap-3 border-t border-slate-200/80 pt-6">
                  {lastSaved ? <span className="text-sm font-semibold text-emerald-600 motion-safe:animate-bp-fade-in">Saved.</span> : null}
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-brand-gradient px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:shadow-card-hover hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </GovernanceCrawlingSettingsForm>
        </div>

        <div className={`${BP_CARD_CLASSES} space-y-4 p-6 motion-safe:animate-bp-fade-up [--bp-delay:120ms]`}>
          <div>
            <h2 className="m-0 text-base font-bold text-slate-900">Crawl content</h2>
            <p className="mt-1 text-sm text-slate-500">Enter one or more URLs, then create a crawl task group for those pages.</p>
          </div>

          {crawlSubmitError ? <Alert variant="danger">{crawlSubmitError}</Alert> : null}

          <div className="space-y-4 rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
            <GovernanceCrawlContentUrlInputForm
              versionKey={`crawl-url-input-${crawlInputVersion}`}
              disabled={crawlSubmitting}
              onAdd={onAddCrawlUrl}
            />

            {!crawlUrls.length ? (
              <div className="rounded-lg bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">No crawl URLs added yet.</div>
            ) : null}

            {crawlUrls.length ? (
              <div className="space-y-3">
                {crawlUrls.map((url) => (
                  <GovernanceOnboardingListItem key={url} onDelete={() => onRemoveCrawlUrl(url)}>
                    <div className="col-span-12 md:col-span-10 text-left">
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate font-eyebrow text-[0.8125rem] font-medium text-slate-700 no-underline transition-colors hover:text-primary hover:no-underline"
                      >
                        {url}
                      </a>
                    </div>
                  </GovernanceOnboardingListItem>
                ))}
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t border-slate-200/80 pt-4">
              <span className="text-sm text-slate-500">
                {crawlUrls.length} URL{crawlUrls.length === 1 ? '' : 's'} selected
              </span>
              <div className="flex items-center gap-2">
                {crawlTaskGroupId ? (
                  <button
                    type="button"
                    className={BP_TOOLBAR_BUTTON_CLASSES}
                    onClick={() => navigate(`/brand-presence/jobs/groups/${encodeURIComponent(String(crawlTaskGroupId))}`)}
                    disabled={crawlSubmitting}
                  >
                    View progress
                  </button>
                ) : null}
                <button
                  type="button"
                  className={BP_PRIMARY_BUTTON_CLASSES}
                  onClick={async () => {
                    if (!apiContext?.api || !project.id) {
                      setCrawlSubmitError('Project context is missing.');
                      return;
                    }

                    if (!crawlUrls.length) {
                      setCrawlSubmitError('Please add at least one URL.');
                      return;
                    }

                    setCrawlSubmitting(true);
                    setCrawlSubmitError(undefined);
                    setCrawlTaskGroupId(undefined);

                    try {
                      const projectId = project.id as InternalId;
                      const site = await resolveProjectSite({ api: apiContext.api, projectId });
                      if (!site) {
                        throw new Error('Unable to resolve a site for the current project.');
                      }

                      const draft = new ClientTaskGroupDraft();
                      draft.type = TaskGroupType.Crawl;
                      draft.urls = Array.from(new Set(crawlUrls));
                      Object.assign(draft, {
                        site: new StaticReference(site),
                        project: new StaticReference({ id: projectId }),
                      });

                      const taskGroup = await apiContext.api.service.taskGroups.create(draft);
                      setCrawlTaskGroupId(taskGroup.id as InternalId);
                    } catch (error) {
                      setCrawlSubmitError((error as Error)?.message || 'Unable to create crawl task group.');
                    } finally {
                      setCrawlSubmitting(false);
                    }
                  }}
                  disabled={crawlSubmitting || !crawlUrls.length}
                >
                  <span className="inline-flex items-center gap-2">
                    {crawlSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                    <span>{crawlSubmitting ? 'Submitting…' : 'Submit'}</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
